import { useEffect, useMemo, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Search, Phone, Mail, Building2, Flame, Snowflake, Sun, Plus, X,
  CalendarClock, CheckCircle2, Circle, AlertTriangle, TrendingUp,
  DollarSign, Target, Users, ArrowRight, StickyNote, ListChecks,
  Activity, ExternalLink, MapPin, Globe
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────
type Stage =
  | "new_lead" | "qualified" | "contacted" | "discovery_scheduled"
  | "discovery_completed" | "proposal_sent" | "negotiation" | "won" | "lost";

interface Lead {
  id: string;
  name: string;
  company: string | null;
  email: string | null;
  phone: string | null;
  source: string | null;
  segment: string | null;
  status: Stage;
  lead_score: number;
  visitor_score: number;
  services_interested: string[];
  tags: string[];
  notes: string | null;
  visitor_id: string | null;
  submission_id: string | null;
  appointment_id: string | null;
  deal_value: number | null;
  lost_reason: string | null;
  next_follow_up_at: string | null;
  last_activity_at: string;
  won_at: string | null;
  created_at: string;
}

interface Activity {
  id: string;
  lead_id: string;
  activity_type: string;
  title: string;
  body: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

interface Task {
  id: string;
  lead_id: string;
  task_type: string;
  title: string;
  description: string | null;
  due_at: string | null;
  completed_at: string | null;
  created_at: string;
}

const STAGES: { id: Stage; label: string; color: string }[] = [
  { id: "new_lead", label: "New Lead", color: "hsl(220 14% 50%)" },
  { id: "qualified", label: "Qualified", color: "hsl(207 68% 55%)" },
  { id: "contacted", label: "Contacted", color: "hsl(190 70% 50%)" },
  { id: "discovery_scheduled", label: "Discovery Scheduled", color: "hsl(260 60% 60%)" },
  { id: "discovery_completed", label: "Discovery Completed", color: "hsl(280 60% 60%)" },
  { id: "proposal_sent", label: "Proposal Sent", color: "hsl(38 90% 55%)" },
  { id: "negotiation", label: "Negotiation", color: "hsl(20 85% 55%)" },
  { id: "won", label: "Won", color: "hsl(142 70% 45%)" },
  { id: "lost", label: "Lost", color: "hsl(0 65% 50%)" },
];

const stageMeta = (s: Stage) => STAGES.find((x) => x.id === s) || STAGES[0];

const temperatureOf = (score: number) =>
  score >= 60 ? "hot" : score >= 25 ? "warm" : "cold";

const TempIcon = ({ score }: { score: number }) => {
  const t = temperatureOf(score);
  if (t === "hot") return <Flame size={12} className="text-orange-400" />;
  if (t === "warm") return <Sun size={12} className="text-yellow-400" />;
  return <Snowflake size={12} className="text-sky-400" />;
};

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ", ";

const fmtDateTime = (d?: string | null) =>
  d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ", ";

const daysSince = (d: string) =>
  Math.floor((Date.now() - new Date(d).getTime()) / 86400000);

// ─────────────────────────────────────────────────────────────
// Main tab
// ─────────────────────────────────────────────────────────────
const RevenuePipelineTab = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<"pipeline" | "followups" | "revenue" | "tasks">("pipeline");
  const [search, setSearch] = useState("");
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [{ data: ld }, { data: tk }] = await Promise.all([
      supabase.from("leads").select("*").order("last_activity_at", { ascending: false }),
      supabase.from("lead_tasks").select("*").order("due_at", { ascending: true, nullsFirst: false }),
    ]);
    setLeads((ld || []) as Lead[]);
    setTasks((tk || []) as Task[]);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Leads/tasks excluded from Realtime for PII protection. Poll while visible.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") fetchAll();
    }, 30_000);
    const onVis = () => { if (document.visibilityState === "visible") fetchAll(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { window.clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [fetchAll]);

  // Search filter
  const filteredLeads = useMemo(() => {
    if (!search.trim()) return leads;
    const q = search.toLowerCase();
    return leads.filter((l) =>
      [l.name, l.company, l.email, l.phone, l.notes, ...(l.tags || []), ...(l.services_interested || [])]
        .filter(Boolean).join(" ").toLowerCase().includes(q)
    );
  }, [leads, search]);

  // Stats for top bar
  const stats = useMemo(() => {
    const open = leads.filter((l) => l.status !== "won" && l.status !== "lost");
    const won = leads.filter((l) => l.status === "won");
    const lost = leads.filter((l) => l.status === "lost");
    const pipelineValue = open.reduce((s, l) => s + (l.deal_value || 0), 0);
    const wonValue = won.reduce((s, l) => s + (l.deal_value || 0), 0);
    const closed = won.length + lost.length;
    const conversion = closed > 0 ? Math.round((won.length / closed) * 100) : 0;
    const hot = open.filter((l) => l.lead_score >= 60).length;
    return { open: open.length, won: won.length, lost: lost.length, pipelineValue, wonValue, conversion, hot };
  }, [leads]);

  const selectedLead = leads.find((l) => l.id === selectedLeadId) || null;

  return (
    <div className="space-y-6">
      {/* Header / Stats */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard icon={<Users size={14} />} label="Open Leads" value={stats.open} tint="hsl(207 68% 60%)" />
        <StatCard icon={<Flame size={14} />} label="Hot Leads" value={stats.hot} tint="hsl(20 85% 60%)" />
        <StatCard icon={<DollarSign size={14} />} label="Pipeline Value" value={`$${stats.pipelineValue.toLocaleString()}`} tint="hsl(142 70% 55%)" />
        <StatCard icon={<Target size={14} />} label="Conversion Rate" value={`${stats.conversion}%`} tint="hsl(260 70% 65%)" />
        <StatCard icon={<TrendingUp size={14} />} label="Won Revenue" value={`$${stats.wonValue.toLocaleString()}`} tint="hsl(142 70% 55%)" />
      </div>

      {/* View switcher + search */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1 rounded-lg border p-1" style={{ borderColor: "hsl(0 0% 16%)", background: "hsl(0 0% 6%)" }}>
          {[
            { id: "pipeline", label: "Pipeline" },
            { id: "followups", label: "Follow-up Center" },
            { id: "revenue", label: "Revenue" },
            { id: "tasks", label: "Tasks" },
          ].map((v) => (
            <button key={v.id} onClick={() => setView(v.id as typeof view)}
              className="rounded-md px-3 py-1.5 text-xs font-medium transition-colors"
              style={{
                background: view === v.id ? "hsl(207 68% 50% / 0.15)" : "transparent",
                color: view === v.id ? "hsl(207 68% 70%)" : "hsl(0 0% 60%)",
              }}>
              {v.label}
            </button>
          ))}
        </div>
        <div className="relative w-full max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search leads, companies, emails, notes…"
            className="pl-9 bg-[hsl(0_0%_6%)] border-[hsl(0_0%_16%)] text-sm" />
        </div>
      </div>

      {loading ? (
        <p className="py-12 text-center text-sm" style={{ color: "hsl(0 0% 50%)" }}>Loading pipeline…</p>
      ) : view === "pipeline" ? (
        <PipelineKanban leads={filteredLeads} onSelect={setSelectedLeadId} />
      ) : view === "followups" ? (
        <FollowUpCenter leads={filteredLeads} tasks={tasks} onSelect={setSelectedLeadId} />
      ) : view === "revenue" ? (
        <RevenueDashboard leads={leads} />
      ) : (
        <TaskList tasks={tasks} leads={leads} onSelectLead={setSelectedLeadId} onRefresh={fetchAll} />
      )}

      {selectedLead && (
        <LeadDrawer
          lead={selectedLead}
          onClose={() => setSelectedLeadId(null)}
          onRefresh={fetchAll}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Stat Card
// ─────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, tint }: { icon: React.ReactNode; label: string; value: string | number; tint: string }) => (
  <div className="rounded-xl border p-4" style={{ background: "hsl(0 0% 8%)", borderColor: "hsl(0 0% 16%)" }}>
    <div className="flex items-center gap-1.5 text-xs" style={{ color: tint }}>
      {icon} {label}
    </div>
    <p className="mt-1 text-xl font-bold" style={{ color: "hsl(0 0% 95%)" }}>{value}</p>
  </div>
);

// ─────────────────────────────────────────────────────────────
// Pipeline Kanban
// ─────────────────────────────────────────────────────────────
const PipelineKanban = ({ leads, onSelect }: { leads: Lead[]; onSelect: (id: string) => void }) => {
  const onDrop = async (e: React.DragEvent, stage: Stage) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    if (!id) return;
    const { error } = await supabase.from("leads").update({ status: stage }).eq("id", id);
    if (error) toast.error("Failed to update stage");
    else toast.success(`Moved to ${stageMeta(stage).label}`);
  };

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-7">
      {STAGES.map((stage) => {
        const items = leads.filter((l) => l.status === stage.id);
        const value = items.reduce((s, l) => s + (l.deal_value || 0), 0);
        return (
          <div key={stage.id}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => onDrop(e, stage.id)}
            className="min-w-0 rounded-xl border"
            style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
            <div className="flex items-center justify-between px-3 py-2.5 border-b" style={{ borderColor: "hsl(0 0% 14%)" }}>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ background: stage.color }} />
                <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: "hsl(0 0% 80%)" }}>{stage.label}</p>
                <span className="text-xs" style={{ color: "hsl(0 0% 45%)" }}>{items.length}</span>
              </div>
              {value > 0 && <span className="text-[10px]" style={{ color: "hsl(142 70% 60%)" }}>${value.toLocaleString()}</span>}
            </div>
            <div className="p-2 space-y-2 max-h-[68vh] overflow-y-auto">
              {items.length === 0 && (
                <p className="px-2 py-6 text-center text-xs" style={{ color: "hsl(0 0% 30%)" }}>Empty</p>
              )}
              {items.map((l) => (
                <div key={l.id}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData("text/plain", l.id)}
                  onClick={() => onSelect(l.id)}
                  className="cursor-pointer rounded-lg border p-3 transition-colors hover:border-[hsl(207_68%_50%/0.4)]"
                  style={{ background: "hsl(0 0% 9%)", borderColor: "hsl(0 0% 16%)" }}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold" style={{ color: "hsl(0 0% 92%)" }}>{l.name}</p>
                      {l.company && (
                        <p className="truncate text-xs flex items-center gap-1 mt-0.5" style={{ color: "hsl(0 0% 55%)" }}>
                          <Building2 size={10} /> {l.company}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <TempIcon score={l.lead_score} />
                      <span className="text-[10px] font-bold" style={{ color: "hsl(0 0% 70%)" }}>{l.lead_score}</span>
                    </div>
                  </div>
                  {l.services_interested?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {l.services_interested.slice(0, 2).map((s) => (
                        <span key={s} className="rounded px-1.5 py-0.5 text-[9px]"
                          style={{ background: "hsl(207 68% 50% / 0.1)", color: "hsl(207 68% 70%)" }}>{s}</span>
                      ))}
                    </div>
                  )}
                  <div className="mt-2 flex items-center justify-between text-[10px]" style={{ color: "hsl(0 0% 40%)" }}>
                    <span>{l.source || ", "}</span>
                    {l.next_follow_up_at && (
                      <span className="flex items-center gap-1"
                        style={{ color: new Date(l.next_follow_up_at) < new Date() ? "hsl(0 70% 60%)" : "hsl(38 80% 60%)" }}>
                        <CalendarClock size={9} /> {fmtDate(l.next_follow_up_at)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Follow-up Center
// ─────────────────────────────────────────────────────────────
const FollowUpCenter = ({ leads, tasks, onSelect }: { leads: Lead[]; tasks: Task[]; onSelect: (id: string) => void }) => {
  const now = Date.now();
  const open = leads.filter((l) => l.status !== "won" && l.status !== "lost");

  const overdue = open.filter((l) => l.next_follow_up_at && new Date(l.next_follow_up_at).getTime() < now);
  const today = open.filter((l) => {
    if (!l.next_follow_up_at) return false;
    const d = new Date(l.next_follow_up_at);
    return d.toDateString() === new Date().toDateString();
  });
  const upcoming = open.filter((l) => l.next_follow_up_at && new Date(l.next_follow_up_at).getTime() > now &&
    new Date(l.next_follow_up_at).toDateString() !== new Date().toDateString());
  const uncontacted = open.filter((l) => l.status === "new_lead" && daysSince(l.created_at) >= 1);
  const hotNoAction = open.filter((l) => l.lead_score >= 60 && daysSince(l.last_activity_at) >= 2);
  const missed = open.filter((l) => daysSince(l.last_activity_at) >= 14);

  const Section = ({ title, items, tint, icon }: { title: string; items: Lead[]; tint: string; icon: React.ReactNode }) => (
    <div className="rounded-xl border" style={{ background: "hsl(0 0% 7%)", borderColor: "hsl(0 0% 16%)" }}>
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "hsl(0 0% 14%)" }}>
        <div className="flex items-center gap-2" style={{ color: tint }}>
          {icon}
          <p className="text-sm font-semibold">{title}</p>
        </div>
        <span className="text-xs" style={{ color: "hsl(0 0% 50%)" }}>{items.length}</span>
      </div>
      <div className="divide-y" style={{ borderColor: "hsl(0 0% 12%)" }}>
        {items.length === 0 ? (
          <p className="px-4 py-6 text-center text-xs" style={{ color: "hsl(0 0% 35%)" }}>None</p>
        ) : items.slice(0, 8).map((l) => (
          <button key={l.id} onClick={() => onSelect(l.id)}
            className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left transition-colors hover:bg-[hsl(0_0%_10%)]">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium" style={{ color: "hsl(0 0% 90%)" }}>{l.name}</p>
              <p className="truncate text-xs" style={{ color: "hsl(0 0% 50%)" }}>
                {l.company || l.email} · {stageMeta(l.status).label}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <TempIcon score={l.lead_score} />
              <ArrowRight size={12} className="text-muted-foreground" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
      <Section title="Overdue Follow-ups" items={overdue} tint="hsl(0 70% 60%)" icon={<AlertTriangle size={14} />} />
      <Section title="Today's Follow-ups" items={today} tint="hsl(38 85% 60%)" icon={<CalendarClock size={14} />} />
      <Section title="Upcoming" items={upcoming} tint="hsl(207 68% 60%)" icon={<CalendarClock size={14} />} />
      <Section title="Uncontacted Leads (1d+)" items={uncontacted} tint="hsl(260 60% 65%)" icon={<Circle size={14} />} />
      <Section title="🔥 Hot Leads, No Action 2d+" items={hotNoAction} tint="hsl(20 85% 60%)" icon={<Flame size={14} />} />
      <Section title="Missed Opportunities (14d+)" items={missed} tint="hsl(0 0% 60%)" icon={<AlertTriangle size={14} />} />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Revenue Dashboard
// ─────────────────────────────────────────────────────────────
const RevenueDashboard = ({ leads }: { leads: Lead[] }) => {
  const won = leads.filter((l) => l.status === "won");
  const lost = leads.filter((l) => l.status === "lost");
  const proposalSent = leads.filter((l) => ["proposal_sent", "negotiation", "won", "lost"].includes(l.status));
  const proposalConv = proposalSent.length > 0 ? Math.round((won.length / proposalSent.length) * 100) : 0;
  const appts = leads.filter((l) => l.appointment_id).length;

  // Stage distribution
  const stageDist = STAGES.map((s) => ({
    ...s,
    count: leads.filter((l) => l.status === s.id).length,
    value: leads.filter((l) => l.status === s.id).reduce((sum, l) => sum + (l.deal_value || 0), 0),
  }));
  const maxCount = Math.max(...stageDist.map((s) => s.count), 1);

  // Source performance
  const sourceMap = new Map<string, { total: number; won: number; value: number }>();
  leads.forEach((l) => {
    const src = l.source || "unknown";
    const cur = sourceMap.get(src) || { total: 0, won: 0, value: 0 };
    cur.total += 1;
    if (l.status === "won") { cur.won += 1; cur.value += l.deal_value || 0; }
    sourceMap.set(src, cur);
  });
  const sources = Array.from(sourceMap.entries()).sort((a, b) => b[1].total - a[1].total);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard icon={<CheckCircle2 size={14} />} label="Deals Won" value={won.length} tint="hsl(142 70% 55%)" />
        <StatCard icon={<X size={14} />} label="Deals Lost" value={lost.length} tint="hsl(0 65% 55%)" />
        <StatCard icon={<CalendarClock size={14} />} label="Appointments" value={appts} tint="hsl(207 68% 60%)" />
        <StatCard icon={<Target size={14} />} label="Proposal Conv." value={`${proposalConv}%`} tint="hsl(260 70% 65%)" />
      </div>

      <div className="rounded-xl border p-5" style={{ background: "hsl(0 0% 7%)", borderColor: "hsl(0 0% 16%)" }}>
        <p className="text-sm font-semibold mb-4" style={{ color: "hsl(0 0% 90%)" }}>Stage Distribution</p>
        <div className="space-y-2">
          {stageDist.map((s) => (
            <div key={s.id} className="flex items-center gap-3">
              <p className="w-44 truncate text-xs" style={{ color: "hsl(0 0% 70%)" }}>{s.label}</p>
              <div className="flex-1 h-6 rounded overflow-hidden" style={{ background: "hsl(0 0% 10%)" }}>
                <div className="h-full transition-all" style={{ width: `${(s.count / maxCount) * 100}%`, background: s.color }} />
              </div>
              <p className="w-12 text-right text-xs font-medium" style={{ color: "hsl(0 0% 85%)" }}>{s.count}</p>
              <p className="w-20 text-right text-[10px]" style={{ color: "hsl(142 70% 60%)" }}>
                {s.value > 0 ? `$${s.value.toLocaleString()}` : ", "}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border p-5" style={{ background: "hsl(0 0% 7%)", borderColor: "hsl(0 0% 16%)" }}>
        <p className="text-sm font-semibold mb-4" style={{ color: "hsl(0 0% 90%)" }}>Lead Source Performance</p>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs" style={{ color: "hsl(0 0% 50%)" }}>
              <th className="text-left pb-2 font-medium">Source</th>
              <th className="text-right pb-2 font-medium">Leads</th>
              <th className="text-right pb-2 font-medium">Won</th>
              <th className="text-right pb-2 font-medium">Conv.</th>
              <th className="text-right pb-2 font-medium">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {sources.length === 0 ? (
              <tr><td colSpan={5} className="py-4 text-center text-xs" style={{ color: "hsl(0 0% 35%)" }}>No data yet</td></tr>
            ) : sources.map(([src, d]) => (
              <tr key={src} className="border-t" style={{ borderColor: "hsl(0 0% 12%)" }}>
                <td className="py-2" style={{ color: "hsl(0 0% 85%)" }}>{src}</td>
                <td className="py-2 text-right" style={{ color: "hsl(0 0% 70%)" }}>{d.total}</td>
                <td className="py-2 text-right" style={{ color: "hsl(142 70% 60%)" }}>{d.won}</td>
                <td className="py-2 text-right" style={{ color: "hsl(0 0% 70%)" }}>{d.total > 0 ? Math.round((d.won / d.total) * 100) : 0}%</td>
                <td className="py-2 text-right" style={{ color: "hsl(142 70% 60%)" }}>${d.value.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Task List
// ─────────────────────────────────────────────────────────────
const TaskList = ({ tasks, leads, onSelectLead, onRefresh }: { tasks: Task[]; leads: Lead[]; onSelectLead: (id: string) => void; onRefresh: () => void }) => {
  const completeTask = async (id: string) => {
    const { error } = await supabase.from("lead_tasks").update({ completed_at: new Date().toISOString() }).eq("id", id);
    if (error) toast.error("Failed");
    else { toast.success("Marked complete"); onRefresh(); }
  };

  const open = tasks.filter((t) => !t.completed_at);
  const done = tasks.filter((t) => t.completed_at).slice(0, 10);

  const row = (t: Task) => {
    const lead = leads.find((l) => l.id === t.lead_id);
    const overdue = t.due_at && !t.completed_at && new Date(t.due_at) < new Date();
    return (
      <div key={t.id} className="flex items-center gap-3 px-4 py-2.5 border-b" style={{ borderColor: "hsl(0 0% 12%)" }}>
        <button onClick={() => !t.completed_at && completeTask(t.id)}
          className="shrink-0" disabled={!!t.completed_at}>
          {t.completed_at ? <CheckCircle2 size={16} className="text-green-500" /> : <Circle size={16} className="text-muted-foreground hover:text-green-500" />}
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm" style={{ color: t.completed_at ? "hsl(0 0% 45%)" : "hsl(0 0% 90%)", textDecoration: t.completed_at ? "line-through" : "none" }}>
            {t.title}
          </p>
          {lead && (
            <button onClick={() => onSelectLead(lead.id)} className="truncate text-xs hover:underline" style={{ color: "hsl(207 68% 60%)" }}>
              {lead.name} {lead.company ? `· ${lead.company}` : ""}
            </button>
          )}
        </div>
        <span className="text-xs shrink-0" style={{ color: overdue ? "hsl(0 70% 60%)" : "hsl(0 0% 50%)" }}>
          {t.due_at ? fmtDateTime(t.due_at) : "No due date"}
        </span>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border" style={{ background: "hsl(0 0% 7%)", borderColor: "hsl(0 0% 16%)" }}>
        <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: "hsl(0 0% 14%)" }}>
          <ListChecks size={14} style={{ color: "hsl(207 68% 60%)" }} />
          <p className="text-sm font-semibold" style={{ color: "hsl(0 0% 90%)" }}>Open Tasks ({open.length})</p>
        </div>
        {open.length === 0 ? <p className="px-4 py-6 text-center text-xs" style={{ color: "hsl(0 0% 35%)" }}>No open tasks</p> : open.map(row)}
      </div>
      {done.length > 0 && (
        <div className="rounded-xl border" style={{ background: "hsl(0 0% 7%)", borderColor: "hsl(0 0% 16%)" }}>
          <div className="px-4 py-3 border-b" style={{ borderColor: "hsl(0 0% 14%)" }}>
            <p className="text-sm font-semibold" style={{ color: "hsl(0 0% 60%)" }}>Recently Completed</p>
          </div>
          {done.map(row)}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Lead Drawer (detail / timeline / quick actions)
// ─────────────────────────────────────────────────────────────
interface VisitorInfo {
  visit_count: number;
  total_page_views: number;
  total_duration_s: number;
  engagement_score: number;
  temperature: string;
  country: string | null;
  city: string | null;
  device: string | null;
  browser: string | null;
  first_seen: string;
  last_seen: string;
}

const LeadDrawer = ({ lead, onClose, onRefresh }: { lead: Lead; onClose: () => void; onRefresh: () => void }) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [visitorEvents, setVisitorEvents] = useState<{ id: string; event_type: string; label: string | null; path: string | null; created_at: string }[]>([]);
  const [visitor, setVisitor] = useState<VisitorInfo | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [taskDraft, setTaskDraft] = useState({ title: "", type: "follow_up", due: "" });
  const [editingDealValue, setEditingDealValue] = useState(false);
  const [dealValue, setDealValue] = useState(lead.deal_value?.toString() || "");
  const [followUpDraft, setFollowUpDraft] = useState(lead.next_follow_up_at ? lead.next_follow_up_at.slice(0, 16) : "");

  const load = useCallback(async () => {
    const [acts, tks, vEvents, vRow] = await Promise.all([
      supabase.from("lead_activities").select("*").eq("lead_id", lead.id).order("created_at", { ascending: false }),
      supabase.from("lead_tasks").select("*").eq("lead_id", lead.id).order("due_at", { ascending: true, nullsFirst: false }),
      lead.visitor_id
        ? supabase.from("visitor_events").select("id,event_type,label,path,created_at").eq("visitor_id", lead.visitor_id).order("created_at", { ascending: false }).limit(30)
        : Promise.resolve({ data: [] }),
      lead.visitor_id
        ? supabase.from("visitors").select("visit_count,total_page_views,total_duration_s,engagement_score,temperature,country,city,device,browser,first_seen,last_seen").eq("visitor_id", lead.visitor_id).maybeSingle()
        : Promise.resolve({ data: null }),
    ]);
    setActivities((acts.data || []) as Activity[]);
    setTasks((tks.data || []) as Task[]);
    setVisitorEvents((vEvents.data || []) as typeof visitorEvents);
    setVisitor((vRow.data as VisitorInfo) || null);
  }, [lead.id, lead.visitor_id]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (status: Stage) => {
    const { error } = await supabase.from("leads").update({ status }).eq("id", lead.id);
    if (error) toast.error("Failed");
    else { toast.success(`Moved to ${stageMeta(status).label}`); onRefresh(); load(); }
  };

  const addNote = async () => {
    if (!noteDraft.trim()) return;
    const { error } = await supabase.from("lead_activities").insert({
      lead_id: lead.id, activity_type: "note", title: "Note", body: noteDraft.trim(),
    });
    if (error) toast.error("Failed");
    else { setNoteDraft(""); toast.success("Note added"); load(); onRefresh(); }
  };

  const addTask = async () => {
    if (!taskDraft.title.trim()) return;
    const { error } = await supabase.from("lead_tasks").insert({
      lead_id: lead.id, title: taskDraft.title.trim(), task_type: taskDraft.type,
      due_at: taskDraft.due ? new Date(taskDraft.due).toISOString() : null,
    });
    if (error) toast.error("Failed");
    else {
      await supabase.from("lead_activities").insert({
        lead_id: lead.id, activity_type: "task", title: `Task created: ${taskDraft.title}`,
      });
      setTaskDraft({ title: "", type: "follow_up", due: "" });
      setShowTaskForm(false);
      toast.success("Task created");
      load(); onRefresh();
    }
  };

  const saveDealValue = async () => {
    const v = parseFloat(dealValue);
    const { error } = await supabase.from("leads").update({ deal_value: isNaN(v) ? null : v }).eq("id", lead.id);
    if (error) toast.error("Failed");
    else { setEditingDealValue(false); toast.success("Saved"); onRefresh(); }
  };

  const saveFollowUp = async () => {
    const { error } = await supabase.from("leads")
      .update({ next_follow_up_at: followUpDraft ? new Date(followUpDraft).toISOString() : null })
      .eq("id", lead.id);
    if (error) toast.error("Failed");
    else { toast.success("Follow-up updated"); onRefresh(); }
  };

  const logCall = async () => {
    await supabase.from("lead_activities").insert({ lead_id: lead.id, activity_type: "call", title: "Call logged" });
    toast.success("Call logged"); load();
  };
  const logEmail = async () => {
    await supabase.from("lead_activities").insert({ lead_id: lead.id, activity_type: "email", title: "Email sent" });
    toast.success("Email logged"); load();
  };

  // Merged timeline
  const timeline = useMemo(() => {
    const a = activities.map((x) => ({ ts: x.created_at, kind: x.activity_type, title: x.title, body: x.body, source: "lead" as const }));
    const v = visitorEvents.map((x) => ({
      ts: x.created_at, kind: x.event_type,
      title: x.label || x.event_type, body: x.path, source: "visitor" as const,
    }));
    return [...a, ...v].sort((x, y) => new Date(y.ts).getTime() - new Date(x.ts).getTime());
  }, [activities, visitorEvents]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl h-full overflow-y-auto border-l" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)" }}>
        {/* Header */}
        <div className="sticky top-0 z-10 border-b px-6 py-4 backdrop-blur" style={{ background: "hsl(0 0% 5% / 0.95)", borderColor: "hsl(0 0% 14%)" }}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold truncate" style={{ color: "hsl(0 0% 95%)" }}>{lead.name}</h2>
                <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                  style={{ background: `${stageMeta(lead.status).color}25`, color: stageMeta(lead.status).color }}>
                  {stageMeta(lead.status).label}
                </span>
              </div>
              <p className="text-sm mt-0.5 truncate" style={{ color: "hsl(0 0% 55%)" }}>
                {lead.company ? `${lead.company} · ` : ""}{lead.source || ", "}
              </p>
            </div>
            <button onClick={onClose} className="rounded-md p-1.5 hover:bg-[hsl(0_0%_12%)]"><X size={18} /></button>
          </div>

          {/* Quick actions */}
          <div className="mt-3 flex flex-wrap gap-1.5">
            {lead.phone && (
              <a href={`tel:${lead.phone}`} onClick={logCall}
                className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium"
                style={{ borderColor: "hsl(142 70% 50% / 0.3)", color: "hsl(142 70% 60%)", background: "hsl(142 70% 50% / 0.05)" }}>
                <Phone size={11} /> Call
              </a>
            )}
            {lead.email && (
              <a href={`mailto:${lead.email}`} onClick={logEmail}
                className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium"
                style={{ borderColor: "hsl(207 68% 50% / 0.3)", color: "hsl(207 68% 65%)", background: "hsl(207 68% 50% / 0.05)" }}>
                <Mail size={11} /> Email
              </a>
            )}
            <button onClick={() => setShowTaskForm((s) => !s)}
              className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium hover:bg-[hsl(0_0%_10%)]"
              style={{ borderColor: "hsl(0 0% 20%)", color: "hsl(0 0% 80%)" }}>
              <Plus size={11} /> Task
            </button>
            <select value={lead.status} onChange={(e) => changeStatus(e.target.value as Stage)}
              className="rounded-md border px-2 py-1 text-xs"
              style={{ background: "hsl(0 0% 8%)", borderColor: "hsl(0 0% 20%)", color: "hsl(0 0% 85%)" }}>
              {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Contact */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Field label="Email" value={lead.email} />
            <Field label="Phone" value={lead.phone} />
            <Field label="Source" value={lead.source} />
            <Field label="Segment" value={lead.segment} />
            <Field label="Lead Score" value={`${lead.lead_score}`} />
            <Field label="Created" value={fmtDate(lead.created_at)} />
          </div>

          {/* Deal value + follow-up */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border p-3" style={{ background: "hsl(0 0% 7%)", borderColor: "hsl(0 0% 14%)" }}>
              <p className="text-xs mb-1" style={{ color: "hsl(0 0% 50%)" }}>Deal Value</p>
              {editingDealValue ? (
                <div className="flex gap-1">
                  <Input value={dealValue} onChange={(e) => setDealValue(e.target.value)} placeholder="0" className="h-7 text-sm" />
                  <Button size="sm" onClick={saveDealValue} className="h-7">Save</Button>
                </div>
              ) : (
                <button onClick={() => setEditingDealValue(true)} className="text-sm font-semibold" style={{ color: "hsl(142 70% 60%)" }}>
                  {lead.deal_value ? `$${lead.deal_value.toLocaleString()}` : "Set value"}
                </button>
              )}
            </div>
            <div className="rounded-lg border p-3" style={{ background: "hsl(0 0% 7%)", borderColor: "hsl(0 0% 14%)" }}>
              <p className="text-xs mb-1" style={{ color: "hsl(0 0% 50%)" }}>Next Follow-up</p>
              <div className="flex gap-1">
                <input type="datetime-local" value={followUpDraft} onChange={(e) => setFollowUpDraft(e.target.value)}
                  onBlur={saveFollowUp}
                  className="flex-1 rounded border px-2 py-1 text-xs"
                  style={{ background: "hsl(0 0% 9%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 85%)" }} />
              </div>
            </div>
          </div>

          {/* Visitor Intelligence */}
          {visitor && (
            <div className="rounded-xl border p-4" style={{ background: "hsl(207 68% 50% / 0.04)", borderColor: "hsl(207 68% 50% / 0.2)" }}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold flex items-center gap-2" style={{ color: "hsl(207 68% 70%)" }}>
                  <Activity size={14} /> Visitor Intelligence
                </p>
                <span className="rounded px-2 py-0.5 text-[10px] font-bold uppercase"
                  style={{
                    background: visitor.temperature === "hot" ? "hsl(20 85% 50% / 0.2)" : visitor.temperature === "warm" ? "hsl(38 90% 50% / 0.2)" : "hsl(207 68% 50% / 0.15)",
                    color: visitor.temperature === "hot" ? "hsl(20 85% 65%)" : visitor.temperature === "warm" ? "hsl(38 90% 65%)" : "hsl(207 68% 70%)",
                  }}>
                  {visitor.temperature}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <Mini label="Visits" value={visitor.visit_count} />
                <Mini label="Page Views" value={visitor.total_page_views} />
                <Mini label="Engagement" value={visitor.engagement_score} />
              </div>
              <div className="mt-3 flex flex-wrap gap-3 text-[11px]" style={{ color: "hsl(0 0% 60%)" }}>
                {(visitor.city || visitor.country) && <span className="flex items-center gap-1"><MapPin size={10} /> {[visitor.city, visitor.country].filter(Boolean).join(", ")}</span>}
                {visitor.device && <span className="flex items-center gap-1"><Globe size={10} /> {visitor.device} · {visitor.browser}</span>}
                <span>First seen {fmtDate(visitor.first_seen)}</span>
              </div>
            </div>
          )}

          {/* Add note */}
          <div className="rounded-xl border p-4" style={{ background: "hsl(0 0% 7%)", borderColor: "hsl(0 0% 14%)" }}>
            <div className="flex items-center gap-2 mb-2">
              <StickyNote size={14} style={{ color: "hsl(38 80% 60%)" }} />
              <p className="text-sm font-semibold" style={{ color: "hsl(0 0% 90%)" }}>Add Note</p>
            </div>
            <Textarea value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} placeholder="What happened, next steps…"
              className="text-sm mb-2 bg-[hsl(0_0%_9%)] border-[hsl(0_0%_18%)]" rows={2} />
            <Button size="sm" onClick={addNote} disabled={!noteDraft.trim()}>Save Note</Button>
          </div>

          {/* Add task form */}
          {showTaskForm && (
            <div className="rounded-xl border p-4 space-y-2" style={{ background: "hsl(0 0% 7%)", borderColor: "hsl(0 0% 14%)" }}>
              <p className="text-sm font-semibold" style={{ color: "hsl(0 0% 90%)" }}>New Task</p>
              <Input value={taskDraft.title} onChange={(e) => setTaskDraft({ ...taskDraft, title: e.target.value })} placeholder="Task title" className="bg-[hsl(0_0%_9%)] border-[hsl(0_0%_18%)]" />
              <div className="flex gap-2">
                <select value={taskDraft.type} onChange={(e) => setTaskDraft({ ...taskDraft, type: e.target.value })}
                  className="flex-1 rounded border px-2 py-1.5 text-sm" style={{ background: "hsl(0 0% 9%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 85%)" }}>
                  <option value="call">Call Lead</option>
                  <option value="send_proposal">Send Proposal</option>
                  <option value="follow_up">Follow Up</option>
                  <option value="review">Review Opportunity</option>
                  <option value="meeting">Schedule Meeting</option>
                </select>
                <input type="datetime-local" value={taskDraft.due} onChange={(e) => setTaskDraft({ ...taskDraft, due: e.target.value })}
                  className="flex-1 rounded border px-2 py-1.5 text-sm" style={{ background: "hsl(0 0% 9%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 85%)" }} />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={addTask}>Create Task</Button>
                <Button size="sm" variant="outline" onClick={() => setShowTaskForm(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {/* Open tasks */}
          {tasks.filter((t) => !t.completed_at).length > 0 && (
            <div className="rounded-xl border" style={{ background: "hsl(0 0% 7%)", borderColor: "hsl(0 0% 14%)" }}>
              <div className="px-4 py-2 border-b" style={{ borderColor: "hsl(0 0% 14%)" }}>
                <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: "hsl(0 0% 60%)" }}>Open Tasks</p>
              </div>
              {tasks.filter((t) => !t.completed_at).map((t) => (
                <div key={t.id} className="flex items-center gap-2 px-4 py-2 border-b" style={{ borderColor: "hsl(0 0% 12%)" }}>
                  <button onClick={async () => {
                    await supabase.from("lead_tasks").update({ completed_at: new Date().toISOString() }).eq("id", t.id);
                    load(); onRefresh();
                  }}>
                    <Circle size={14} className="text-muted-foreground hover:text-green-500" />
                  </button>
                  <p className="flex-1 text-sm" style={{ color: "hsl(0 0% 88%)" }}>{t.title}</p>
                  <p className="text-xs" style={{ color: "hsl(0 0% 50%)" }}>{fmtDateTime(t.due_at)}</p>
                </div>
              ))}
            </div>
          )}

          {/* Timeline */}
          <div>
            <p className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: "hsl(0 0% 90%)" }}>
              <Activity size={14} /> Timeline
            </p>
            <div className="space-y-2">
              {timeline.length === 0 ? (
                <p className="text-xs py-4 text-center" style={{ color: "hsl(0 0% 35%)" }}>No activity yet</p>
              ) : timeline.map((e, i) => (
                <div key={i} className="flex gap-3 rounded-lg border p-3"
                  style={{ background: "hsl(0 0% 7%)", borderColor: "hsl(0 0% 14%)" }}>
                  <div className="shrink-0 mt-0.5">
                    <span className="inline-block h-2 w-2 rounded-full"
                      style={{ background: e.source === "visitor" ? "hsl(207 68% 60%)" : "hsl(142 70% 55%)" }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm" style={{ color: "hsl(0 0% 88%)" }}>{e.title}</p>
                    {e.body && <p className="text-xs mt-0.5" style={{ color: "hsl(0 0% 55%)" }}>{e.body}</p>}
                    <p className="text-[10px] mt-1" style={{ color: "hsl(0 0% 40%)" }}>{fmtDateTime(e.ts)} · {e.source}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value }: { label: string; value: string | null | undefined }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wide" style={{ color: "hsl(0 0% 45%)" }}>{label}</p>
    <p className="truncate" style={{ color: "hsl(0 0% 85%)" }}>{value || ", "}</p>
  </div>
);

const Mini = ({ label, value }: { label: string; value: number }) => (
  <div className="rounded p-2" style={{ background: "hsl(0 0% 5% / 0.6)" }}>
    <p className="text-[10px]" style={{ color: "hsl(0 0% 50%)" }}>{label}</p>
    <p className="text-sm font-bold" style={{ color: "hsl(0 0% 92%)" }}>{value}</p>
  </div>
);

export default RevenuePipelineTab;
